import React, {
  createContext, useState, useRef, useMemo, useContext, useCallback,
} from 'react';
import { Animated } from 'react-native';
import { reverse } from 'lodash/fp';
import { useDispatch } from 'react-redux';
import ReactNativeHapticFeedback from 'react-native-haptic-feedback';

import useDimensions from '../../hooks/dimensions';
import MultiSelectContext from '../../contexts/MultiSelect';
import { updateTodoBatch } from '../../modules/todo/actions';


const ActionsContext = createContext({
  leftActions: [],
  rightActions: [],
  actions: [],
});

const MAX_SWIPE_WIDTH = 750;
const COMMIT_ANIMATION_DURATION = 300;

export const Provider = ({
  children, leftActions: la, rightActions: ra, data,
}) => {
  const { width } = useDimensions('window'),
        swipeWidth = width,
        dispatch = useDispatch(),
        { isEnabled, setEnabled } = useContext(MultiSelectContext),
        divideScreenByLeft = la.length + 1,
        divideScreenByRight = ra.length + 1,
        swipeSensitivity = width / Math.min(width, MAX_SWIPE_WIDTH),
        divideScreenBy = Math.max(divideScreenByLeft, divideScreenByRight);


  const [commitedAction, setCommitedAction] = useState(null),
        [commitedItems, setCommitedItems] = useState([]),
        x = useRef(new Animated.Value(0)),
        commitAnimationProgress = useRef(new Animated.Value(0)),
        leftActions = useMemo(() => ra.map((a, i) => {
          const triggerAt = (i + 1) * swipeWidth
          / divideScreenBy;
          return { ...a, triggerAt };
        }), [ra, swipeWidth, divideScreenBy]),
        rightActions = useMemo(() => reverse(la.map((a, i) => {
          const triggerAt = -(i + 1) * swipeWidth
          / divideScreenBy;
          return { ...a, triggerAt };
        })), [la, swipeWidth, divideScreenBy]),
        triggerAction = useCallback((action, left = false, itemIds) => {
          if (itemIds.length === 0) {
            return;
          }

          const commitMany = isEnabled,
                getItem = (id) => data.find((i) => i.id === id);

          setCommitedItems(itemIds);

          const { status } = action;
          ReactNativeHapticFeedback && ReactNativeHapticFeedback.trigger('impactHeavy');

          commitAnimationProgress.current.setValue(0);

          const afterAnimation = async () => {
            const items = itemIds.map((id) => getItem(id));

            if (items.length > 0) {
              try {
                await dispatch(updateTodoBatch(
                  items,
                  { status, manualSortOrder: -new Date().valueOf() },
                ));
              } finally {
                setCommitedAction(null);

                if (commitMany) {
                  setEnabled(false);
                }
              }
            }
          };

          Animated.parallel([
            Animated.timing(x.current, {
              toValue: left ? width : -width,
              useNativeDriver: true,
              duration: COMMIT_ANIMATION_DURATION,
            }),
            Animated.timing(commitAnimationProgress.current, {
              toValue: 1,
              useNativeDriver: true,
              duration: COMMIT_ANIMATION_DURATION,
            }),
          ]).start(afterAnimation);

          setCommitedAction(action);
        }, [data, dispatch, isEnabled, setEnabled, width]);

  return (
    <ActionsContext.Provider value={{
      commitAnimationProgress: commitAnimationProgress.current,
      commitedAction,
      commitedItems,
      swipeSensitivity,
      leftActions,
      rightActions,
      triggerAction,
      setCommitedAction,
      setCommitedItems,
      x: x.current,
    }}
    >
      { children }
    </ActionsContext.Provider>
  );
};

export default ActionsContext;
